import React, { useEffect, useState } from "react";
import "./MultiStepForm.css";
import SelectComponent from "../../ReusableComponents/SelectComponent";
import Loading from "../../ReusableFunctions/Loader";
import { getAllManufacturers, getDSPDrones } from "../../apis/drones";
import { checkOnboardingStatus } from "../../apis/operator";
import { DRONE_TYPES, SERVICE_TYPES } from "../../appConstants";

const drone_payloads = ["5L", "6L", "8L", "10L", "16L", "20L"];
const AddDrone = React.forwardRef(({ onNext }, ref) => {
  const [droneDetails, setDroneDetails] = useState({
    name: "",
    type: "",
    model_name: "",
    service_type: undefined,
    manufacturer: undefined,
    endurance: "",
    payload: "",
    battary_capacity: "",
    drone_id: "",
    certified: undefined,
    otherManufacturer: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [locationId, setLocationId] = useState(null);
  const [manufacturers, setManufacturers] = useState([]);
  const [showOtherManufacturerInput, setShowOtherManufacturerInput] =
    useState(false);

  const [initalData, setInitalData] = useState({
    name: "",
    type: "",
    model_name: "",
    service_type: {
      label: "Spraying service",
      value: "Spraying service",
    },
    manufacturer: undefined,
    endurance: "",
    payload: "",
    battary_capacity: "",
    drone_id: "",
    certified: undefined,
    otherManufacturer: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    type: "",
    endurance: "",
    payload: "",
    model_name: "",
    battary_capacity: "",
    drone_id: "",
    service_type: {
      label: "Spraying service",
      value: "Spraying service",
    },
    manufacturer: "",
    certified: "",
    otherManufacturer: "",
  });

  const getDspDronesFunc = async () => {
    Loading.set(true);
    try {
      const drones = await getDSPDrones();
      const emptyDroneData = {
        name: "",
        type: "",
        endurance: "",
        payload: "",
        battary_capacity: "",
        drone_id: "",
        service_type: {
          label: "Spraying service",
          value: "Spraying service",
        },
        manufacturer: undefined,
        certified: undefined,
        otherManufacturer: "",
      };

      if (drones.length <= 0) {
        setDroneDetails(emptyDroneData);
        setInitalData(emptyDroneData);
        return;
      }

      const firstDrone = drones[0];
      const formattedDroneData = {
        id: firstDrone.id || undefined,
        name: firstDrone.name || "",
        type: firstDrone.type
          ? { label: firstDrone.type, value: firstDrone.type }
          : "",
        endurance: firstDrone.endurance || "",
        payload: firstDrone.battery_capacity
          ? {
              label: firstDrone.battery_capacity,
              value: firstDrone.battery_capacity,
            }
          : "",
        battary_capacity: firstDrone.battery_capacity || "",
        drone_id: firstDrone.drone_uin || "",
        model_name: firstDrone.model_name || "",
        service_type: firstDrone.service_type
          ? { label: firstDrone.service_type, value: firstDrone.service_type }
          : "",
        manufacturer: firstDrone.manufacturer
          ? { label: firstDrone.manufacturer, value: firstDrone.manufacturer }
          : "",
        certified:
          firstDrone.certified != undefined
            ? {
                label: firstDrone.certified ? "Yes" : "No",
                value: firstDrone.certified,
              }
            : "",
      };

      setDroneDetails(formattedDroneData);
      setInitalData(formattedDroneData);
      setIsEditing(true);
    } catch (err) {
      console.error("Error fetching DSP drones:", err);
    } finally {
      Loading.set(false); // Ensure loading is turned off in all cases
    }
  };

  const getOnBoardingStatusFunc = async () => {
    try {
      const data = await checkOnboardingStatus();
      setLocationId(data.existingLocation?.id);
    } catch (err) {
      console.log("Error fetching onboarding status", err);
    }
  };

  const getAllManufacturersFunc = async () => {
    try {
      const manufacturers = await getAllManufacturers();
      const manufacturersOptions = [{ label: "Other", value: "Other" }];

      // Then add the rest of the manufacturers
      manufacturersOptions.push(
        ...manufacturers.map((manufacturer) => ({
          label: manufacturer.name,
          value: manufacturer.name,
        }))
      );

      setManufacturers(manufacturersOptions);
    } catch (err) {
      console.log("Error fetching manufacturers", err);
    }
  };

  useEffect(() => {
    getAllManufacturersFunc();
    getDspDronesFunc();
    getOnBoardingStatusFunc();
  }, []);

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "name":
        if (!value.trim()) error = "Drone name is required";
        else if (value.length < 3 || value.length > 50)
          error = "drone name should be between 3 to 50 characters";
        break;
      case "type":
        if (!value) error = "Drone type is required";
        break;
      case "service_type":
        if (!value) error = "Service type is required";
        break;
      case "manufacturer":
        if (!value) error = "Manufacturer is required";
        break;
      case "otherManufacturer":
        // Only validate if "Other" is selected in manufacturer dropdown
        if (droneDetails.manufacturer?.value === "Other") {
          if (!value.trim()) {
            error = "Manufacturer name is required";
          } else if (value.length < 3 || value.length > 100) {
            error = "Manufacturer name must be between 3 to 100 characters";
          } else if (value !== value.trim()) {
            error = "Manufacturer name cannot have leading or trailing spaces";
          }
        }
        break;
      case "certified":
        if (!value) error = "Certified status is required";
        break;
      case "endurance":
        if (!value) error = "Endurance is required";
        else if (isNaN(value)) error = "Endurance must be a number";
        else if (value < 1 || value > 99)
          error = "Endurance must be between 1 to 99";
        break;
      case "model_name":
        if (!value || !value.trim()) {
          error = "Model name is required";
        } else if (value.trim().length < 3 || value.trim().length > 30) {
          error = "Model name must be between 3 to 30 characters";
        } else if (!/^[a-zA-Z0-9\s]+$/.test(value.trim())) {
          error = "Model name can only contain letters, numbers and spaces";
        } else if (value.trim() !== value) {
          error = "Model name must not have leading or trailing spaces";
        } else if (/\s{2,}/.test(value)) {
          error = "Model name must not have consecutive spaces";
        }
        break;

      case "payload":
        if (!value) error = "battery capacity is required";
        break;
      case "drone_id":
        if (!value) {
          error = "Drone ID is required";
        } else if (value.length < 3 || value.length > 50) {
          error = "Drone ID should be between 3 to 50 characters";
        } else if (!/^[a-zA-Z0-9]+$/.test(value)) {
          error = "Drone ID can only contain letters and digits";
        }
        break;
      case "battary_capacity":
        if (!value) error = "Battery capacity is required";
        else if (isNaN(value)) error = "Battery capacity must be a number";
        else if (value < 100 || value > 99999)
          error = "Battery capacity must be between 100 to 99999 mAh";
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    return !error;
  };

  const validateForm = () => {
    let isValid = true;

    isValid = validateField("name", droneDetails.name) && isValid;
    isValid = validateField("type", droneDetails.type) && isValid;
    isValid =
      validateField("service_type", droneDetails.service_type) && isValid;
    isValid =
      validateField("manufacturer", droneDetails.manufacturer) && isValid;
    isValid = validateField("certified", droneDetails.certified) && isValid;
    isValid = validateField("endurance", droneDetails.endurance) && isValid;
    isValid = validateField("payload", droneDetails.payload) && isValid;
    isValid =
      validateField("battary_capacity", droneDetails.battary_capacity) &&
      isValid;
    isValid = validateField("drone_id", droneDetails.drone_id) && isValid;
    isValid = validateField("model_name", droneDetails.model_name) && isValid;

    if (droneDetails.manufacturer?.value === "Other") {
      isValid =
        validateField("otherManufacturer", droneDetails.otherManufacturer) &&
        isValid;
    }

    return isValid;
  };

  const handleChange = (name, value) => {
    setDroneDetails((prev) => ({ ...prev, [name]: value }));
    if (name === "manufacturer") {
      setShowOtherManufacturerInput(value?.value === "Other");
      // Clear otherManufacturer field when switching away from "Other"
      if (value?.value !== "Other") {
        setDroneDetails((prev) => ({ ...prev, otherManufacturer: "" }));
        setErrors((prev) => ({ ...prev, otherManufacturer: "" }));
      }
    }
    validateField(name, value);
  };

  React.useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (!validateForm()) {
        return false;
      }
      let manufacturerValue =
        droneDetails.manufacturer?.value === "Other"
          ? droneDetails.otherManufacturer
          : droneDetails.manufacturer?.value;
      let data = {
        ...droneDetails,
        model_name: droneDetails.model_name,
        manufacturer: manufacturerValue,
        service_type: droneDetails.service_type?.value,
        endurance: droneDetails.endurance,
        certified: droneDetails.certified?.value,
        location_id: locationId,
        type: droneDetails.type?.label,
        battery_capacity: droneDetails?.battary_capacity,
        drone_uin: droneDetails.drone_id,
        tank_capacity: droneDetails.payload?.label,
      };

      const hasChanged = initalData
        ? Object.keys(droneDetails).some(
            (key) => droneDetails[key] !== initalData[key]
          )
        : true;

      onNext(hasChanged ? data : null, isEditing);
      return true;
    },
  }));

  return (
    <div className="flex flex-col h-[100%] gap-1 mb-6">
      <div
        style={{
          display: "flex",
          gap: "0.2rem",
          flexDirection: "column",
        }}
      >
        <div>
          <p className="form-title" style={{ marginBottom: "0px" }}>
            Add Drone
          </p>
        </div>
        <span className="font-manrope text-[15px] font-[500] tracking-wide">
          You can add multiple drones from drone section.
        </span>
      </div>

      <div className=" h-[80%] overflow-x-auto flex flex-col gap-[15px] pt-2">
        {/* drone name */}
        <div className="flex flex-col gap-[0.2rem]">
          <label htmlFor="name" className="input-label">
            What is the drone name?<span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            placeholder="Enter drone name"
            value={droneDetails.name}
            onChange={(e) => {
              handleChange("name", e.target.value);
            }}
            required
            style={{
              width: "50%",
              height: "40px",
              padding: "8px 12px",
              borderRadius: "4px",
              border: errors.name ? "1px solid #D92D20" : "1px solid #D0D5DD",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
          {errors.name && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.name}
            </div>
          )}
        </div>

        {/* drone model */}
        <div className="flex flex-col gap-[0.2rem]">
          <label htmlFor="model_name" className="input-label">
            What is the drone model ?<span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            id="model_name"
            name="model_name"
            placeholder="Enter drone model"
            disabled={isEditing}
            value={droneDetails.model_name}
            onChange={(e) => {
              handleChange("model_name", e.target.value);
            }}
            required
            style={{
              width: "50%",
              height: "40px",
              padding: "8px 12px",
              borderRadius: "4px",
              border: errors.model_name
                ? "1px solid #D92D20"
                : "1px solid #D0D5DD",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
          {errors.model_name && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.model_name}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-[0.2rem] w-[50%]">
          <label htmlFor="manufacturer" className="input-label">
            Who is the manufacturer?<span style={{ color: "red" }}>*</span>
          </label>
          <SelectComponent
            controlStyle={{
              height: "40px",
              width: "100%",
              borderRadius: "4px",
              border: errors.manufacturer
                ? "1px solid #D92D20"
                : "1px solid #D0D5DD",
              background: "white",
              fontSize: "14px",
              fontWeight: "500",
              color: "#1E1E1E",
              boxShadow: "none",
            }}
            menuStyles={{
              zIndex: 9999,
            }}
            menuPortalStyles={{
              zIndex: 9999,
            }}
            isDisabled={isEditing}
            isSearchable={true}
            placeholder={"Select manufacturer"}
            menuPlacement={"bottom"}
            list={manufacturers}
            value={
              droneDetails?.manufacturer || {
                label: "Select manufacturer",
                value: undefined,
              }
            }
            onChange={(data) => {
              handleChange("manufacturer", data);
            }}
             menuPortalTarget={document.body}
           menuShouldBlockScroll={true}
          />
          {errors.manufacturer && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.manufacturer}
            </div>
          )}
        </div>

        {showOtherManufacturerInput && (
          <div className="flex flex-col gap-[0.2rem]">
            <label htmlFor="otherManufacturer" className="input-label">
              Manufacturer Name<span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              id="otherManufacturer"
              name="otherManufacturer"
              placeholder="Enter manufacturer name"
              disabled={isEditing}
              value={droneDetails.otherManufacturer}
              onChange={(e) =>
                handleChange("otherManufacturer", e.target.value)
              }
              required
              style={{
                width: "50%",
                height: "40px",
                padding: "8px 12px",
                borderRadius: "4px",
                border: errors.otherManufacturer
                  ? "1px solid #D92D20"
                  : "1px solid #D0D5DD",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
            {errors.otherManufacturer && (
              <div
                style={{
                  color: "#D92D20",
                  fontSize: "12px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {errors.otherManufacturer}
              </div>
            )}
          </div>
        )}

        {/* <div className='flex flex-col gap-[0.2rem] w-[50%]'>
          <label htmlFor='service_type' className='input-label'> What is the service type?<span style={{color:"red"}}>*</span></label>
               <SelectComponent
                      controlStyle={{ 
                        height: "40px", 
                        width: "100%", 
                        borderRadius: "4px", 
                        border: errors.service_type ? "1px solid #D92D20" : "1px solid #D0D5DD", 
                        background: "white", 
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#1E1E1E",
                        boxShadow: "none"
                    }}
                    menuStyles={{
                      zIndex:9999
                    }}
                    menuPortalStyles={{
                      zIndex:9999
                    }}
                      isSearchable={true}
                      isDisabled={isEditing}
                      placeholder={"Select service type"}
                      menuPlacement={"bottom"}
                      list={[{value:"Spraying",label:"Spraying"}]}
                      value={droneDetails?.service_type || {label:"Select service type",value:undefined}}
                      onChange={(data) => {
                         handleChange("service_type", data);
                      }}
                  />
              {errors.service_type && (
                      <div style={{
                          color: "#D92D20",
                          fontSize: "12px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center"
                      }}>
                          {errors.service_type}
                      </div>
                  )}
        </div> */}

        <div className="flex flex-col gap-[0.2rem] w-[50%]">
          <label htmlFor="type" className="input-label">
            What is the drone type?<span style={{ color: "red" }}>*</span>
          </label>
          <SelectComponent
            controlStyle={{
              height: "40px",
              width: "100%",
              borderRadius: "4px",
              border: errors.type ? "1px solid #D92D20" : "1px solid #D0D5DD",
              background: "white",
              fontSize: "14px",
              fontWeight: "500",
              color: "#1E1E1E",
              boxShadow: "none",
            }}
            menuStyles={{
              zIndex: 9999,
            }}
            menuPortalStyles={{
              zIndex: 9999,
            }}
            isDisabled={isEditing}
            isSearchable={true}
            placeholder={"Select drone type"}
            menuPlacement={"bottom"}
            list={DRONE_TYPES?.map((type) => ({
              value: type,
              label: type,
            }))}
            menuPortalTarget={document.body}
           menuShouldBlockScroll={true}
            value={
              droneDetails?.type || { label: "Select type", value: undefined }
            }
            onChange={(data) => {
              handleChange("type", data);
            }}
          />
          {errors.type && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.type}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-[0.2rem]">
          <label htmlFor="battary_capacity" className="input-label">
            What is the battery capacity of selected drone {""} (mAh) ?
            <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            id="battary_capacity"
            name="battary_capacity"
            placeholder="Enter battary capacity in mAh "
            value={droneDetails.battary_capacity}
            disabled={isEditing}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || /^\d+$/.test(value)) {
                handleChange("battary_capacity", e.target.value);
              }
            }}
            required
            style={{
              width: "50%",
              height: "40px",
              padding: "8px 12px",
              borderRadius: "4px",
              border: errors.battary_capacity
                ? "1px solid #D92D20"
                : "1px solid #D0D5DD",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
          {errors.battary_capacity && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.battary_capacity}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-[0.2rem]">
          <label htmlFor="endurance" className="input-label">
            What is the endurance of selected drone {""} (minutes)?
            <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            id="endurance"
            disabled={isEditing}
            name="endurance"
            placeholder="Enter endurance in minutes"
            value={droneDetails.endurance}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || /^\d+$/.test(value)) {
                handleChange("endurance", value);
              }
            }}
            required
            style={{
              width: "50%",
              height: "40px",
              padding: "8px 12px",
              borderRadius: "4px",
              border: errors.endurance
                ? "1px solid #D92D20"
                : "1px solid #D0D5DD",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
          {errors.endurance && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.endurance}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-[0.2rem] w-[50%]">
          <label htmlFor="payload" className="input-label">
            What is the tank capacity ?<span style={{ color: "red" }}>*</span>
          </label>
          <SelectComponent
            controlStyle={{
              height: "40px",
              width: "100%",
              borderRadius: "4px",
              border: errors.payload
                ? "1px solid #D92D20"
                : "1px solid #D0D5DD",
              background: "white",
              fontSize: "14px",
              fontWeight: "500",
              color: "#1E1E1E",
              boxShadow: "none",
            }}
            menuStyles={{
              zIndex: 9999,
            }}
            menuPortalStyles={{
              zIndex: 9999,
            }}
            menuPortalTarget={document.body}
           menuShouldBlockScroll={true}
            isDisabled={isEditing}
            isSearchable={true}
            placeholder={"Select payload"}
            menuPlacement={"bottom"}
            list={drone_payloads.map((payload) => ({
              label: payload,
              value: payload,
            }))}
            value={
              droneDetails?.payload || {
                label: "Select payload",
                value: undefined,
              }
            }
            onChange={(data) => {
              handleChange("payload", data);
            }}
          />
          {errors.payload && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.payload}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-[0.2rem] w-[50%]">
          <label htmlFor="certified" className="input-label">
            Is this drone certified?<span style={{ color: "red" }}>*</span>
          </label>
          <SelectComponent
            controlStyle={{
              height: "40px",
              width: "100%",
              borderRadius: "4px",
              border: errors.certified
                ? "1px solid #D92D20"
                : "1px solid #D0D5DD",
              background: "white",
              fontSize: "14px",
              fontWeight: "500",
              color: "#1E1E1E",
              boxShadow: "none",
            }}
            menuStyles={{
              zIndex: 9999,
            }}
            menuPortalStyles={{
              zIndex: 9999,
            }}
            isDisabled={isEditing}
            isSearchable={true}
            placeholder={"Select certified"}
            menuPlacement={"bottom"}
            list={[
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ]}
            value={
              droneDetails?.certified || {
                label: "Select cerified",
                value: undefined,
              }
            }
            onChange={(data) => {
              handleChange("certified", data);
            }}
            menuPortalTarget={document.body}
           menuShouldBlockScroll={true}
          />
          {errors.certified && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.certified}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-[0.2rem]">
          <label htmlFor="drone_id" className="input-label">
            What is the drone id?<span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            id="drone_id"
            name="drone_id"
            placeholder="Enter DroneID"
            value={droneDetails.drone_id}
            onChange={(e) => {
              handleChange("drone_id", e.target.value);
            }}
            style={{
              width: "50%",
              height: "40px",
              padding: "8px 12px",
              borderRadius: "4px",
              border: errors.drone_id
                ? "1px solid #D92D20"
                : "1px solid #D0D5DD",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
          {errors.drone_id && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.drone_id}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default AddDrone;
